import log from "loglevel";

import { ensureFunction, extend, groupBy, identity, when, get, keys, filter } from "@2l/utils";
import { EventEmitter } from "tsee";
import { ExperimentType, ExperimentToolkit, SplitOptions, AppliedConfig, Context, UserExperiment } from "./interfaces";

const TTL = 36e3; // 1h

export default class ConfigController extends EventEmitter {
  private cookieKeyExperiments = "_ewa.experiments";
  private storageKeyConfig = "_ewa.remote_config";

  layers = [
    ExperimentType.LANDING,
    ExperimentType.CONVERSION,
    ExperimentType.CLASS,
    ExperimentType.STORE,
    ExperimentType.EXTERNAL,
    ExperimentType.PLATFORM_LANDING,
  ];
  activeLayers: ExperimentType[] = [];
  context: Context = {
    userId: "",
    os: null,
  };
  config: AppliedConfig = {
    userExperiments: [],
    values: {},
  };
  syncedAt = 0;
  remoteExperiments: ExperimentToolkit[] = [];

  constructor() {
    super();
    this.config = this.restoreConfig() || {
      userExperiments: [],
      values: {},
    };
  }

  async sha256(message: string) {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    return hashHex;
  }

  async getBucket(userId: string | number, salt: string) {
    const hash = await this.sha256(userId + salt);
    const short1 = hash.slice(0, 16);
    const int = BigInt(`0x${short1}`);

    return Number(int % BigInt(100));
  }

  async split(options: SplitOptions) {
    const { userId, experimentId, experimentOptions } = options;

    const groups = keys(experimentOptions);
    const position = (await this.getBucket(userId, experimentId)) * groups.length;
    const commonMultiple = groups.length * 99;
    const range = (commonMultiple + groups.length) / groups.length;

    for (let i = 0; i < groups.length; i++) {
      const min = i * range;
      const max = i * range + range - 1;

      if (this.isInRange(position, [min, max])) {
        return groups[i];
      }
    }
  }

  isInRange(x: number, range: string[] | number[]) {
    const [min, max] = range;
    return x >= +min && x <= +max;
  }

  async init(payload: Context) {
    this.context = payload;
    await this.sync();
  }

  async sync() {
    let experimentsConfig;

    try {
      experimentsConfig = await this.getCloudTests();
      this.remoteExperiments = experimentsConfig;
    } catch (e) {
      log.error(e);
      return;
    }

    return this.applyConfig(experimentsConfig);
  }

  async attachLayer(layer: string, payload: Context, externalId?: string | null | undefined) {
    if (!this.activeLayers.includes(layer as ExperimentType)) {
      this.activeLayers.push(layer as ExperimentType);
    }

    const userId = externalId || "";
    this.context = {
      ...payload,
      userId: hashFnv32a(userId),
    };

    this.applyConfig(this.remoteExperiments);

    await this.sync();
  }

  async applyConfig(cfg: ExperimentToolkit[]) {
    if (!cfg) cfg = this.remoteExperiments;

    const layers = groupBy("type", filter({ status: "running", type: this.activeLayers }, cfg));

    const appliedConfig: AppliedConfig = {
      userExperiments: [],
      values: {},
    };
    const { userId, nativeLanguage } = this.context;

    for (const l in layers) {
      const layerGroup = layers[l];

      for (const e in layerGroup) {
        const experiment: ExperimentToolkit = layerGroup[e];
        const { buckets, type } = experiment;

        const userBucket = await this.getBucket(userId, type);

        const check: (ctx: any) => boolean = ensureFunction(experiment.conditions_dict || identity(), when);

        const shouldJoin = buckets.includes(userBucket);

        if (shouldJoin && check(this.context)) {
          const group = await this.split({
            experimentId: experiment.id,
            layer: experiment.type,
            experimentOptions: experiment.values,
            userId,
          });

          if (!group) continue;

          appliedConfig.userExperiments = [
            ...appliedConfig.userExperiments,
            {
              id: experiment.id,
              group,
              layer: experiment.type,
              value: experiment.values[group],
              conditions: experiment.conditions_dict ?? {},
            },
          ];

          appliedConfig.values = extend(appliedConfig.values, experiment.values[group]);

          appliedConfig[experiment.type] = experiment.values[group];

          appliedConfig.nativeLanguage = nativeLanguage;
        }
      }
    }

    this.config = appliedConfig;
    this.syncedAt = Date.now();
    localStorage.setItem(this.storageKeyConfig, JSON.stringify(this.config));

    this.emit("sync", {
      config: this.config,
    });
  }

  getRemoteConfig(): { [key: string]: any } {
    return this.config;
  }

  async syncAndGetRemoteConfig(force = false): Promise<{ [key: string]: any }> {
    if (Date.now() - this.syncedAt >= TTL || force) await this.sync();

    return this.config;
  }

  private restoreConfig(): AppliedConfig | undefined {
    const value = localStorage.getItem(this.storageKeyConfig);

    if (!value) return;

    try {
      return JSON.parse(value);
    } catch (e) {
      log.error(e);
    }
  }

  async getCloudTests(): Promise<ExperimentToolkit[]> {
    let config = [];
    try {
      const response = await fetch(`${__API_URL__}/api/v25/settings`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const { result } = await response.json();
      const cloudTestsWeb = get("settings.cloudTestsWeb", result);
      config = JSON.parse(cloudTestsWeb || "[]");
    } catch (e) {
      console.error(e);
    }

    return config;
  }
}

/**
 * Calculate a 32 bit FNV-1a hash
 * Found here: https://gist.github.com/vaiorabbit/5657561
 * Ref.: http://isthe.com/chongo/tech/comp/fnv/
 *
 * @param {string} str the input value
 * @param {integer} [seed] optionally pass the hash of the previous chunk
 * @returns {integer}
 */
function hashFnv32a(str: string, seed?: number) {
  /*jshint bitwise:false */
  var i,
    l,
    hval = seed === undefined ? 0x811c9dc5 : seed;

  for (i = 0, l = str.length; i < l; i++) {
    hval ^= str.charCodeAt(i);
    hval += (hval << 1) ^ (hval << 4) ^ (hval << 7) ^ (hval << 8) ^ (hval << 24);
  }

  return hval >>> 0;
}
